import { Clock, ListChecks, Lightbulb, Tag, Check, Pencil } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const TYPE_COLORS = {
    technical:   'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/25',
    behavioral:  'bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/25',
    situational: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25',
    cultural:    'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25',
    coding:      'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/25',
};
const DIFF_COLORS = {
    easy:   'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25',
    medium: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25',
    hard:   'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/25',
};
function secsToLabel(s) {
    const n = Number(s) || 0;
    if (n < 60) return `${n}s`;
    const m = Math.floor(n / 60), r = n % 60;
    return r === 0 ? `${m} min` : `${m}m ${r}s`;
}

/**
 * QuestionPreviewModal — read-only preview of all questions in a set.
 *
 * Props:
 *   open      boolean
 *   onClose   () => void
 *   set       full QuestionSet object (with questions[])
 *   onEdit    () => void — opens the editor for this set
 */
export function QuestionPreviewModal({ open, onClose, set, onEdit }) {
    if (!set) return null;
    const questions = set.questions || [];

    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-h-[92vh] w-[95vw] overflow-hidden flex flex-col sm:max-w-3xl p-0">
                {/* Header */}
                <DialogHeader className="px-6 pt-5 pb-4 pr-14 border-b border-border flex-shrink-0">
                    <div className="flex items-start justify-between gap-3 pr-2">
                        <div className="min-w-0">
                            <DialogTitle className="text-base font-semibold truncate">{set.name}</DialogTitle>
                            <DialogDescription className="text-xs mt-0.5">
                                {questions.length} question{questions.length !== 1 ? 's' : ''}
                                {set.interviewTypeName && <> · {set.interviewTypeName}</>}
                                {set.difficultyName && <> · {set.difficultyName}</>}
                            </DialogDescription>
                        </div>
                        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs shrink-0"
                            onClick={() => { onClose(); onEdit?.(); }}>
                            <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                    </div>
                </DialogHeader>

                {/* Question list */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                    {questions.length === 0 && (
                        <p className="py-12 text-center text-sm text-muted-foreground">No questions in this set.</p>
                    )}

                    {questions.map((q, idx) => (
                        <div key={String(q._id || idx)}
                            className="rounded-xl border border-border bg-card p-4 space-y-3">
                            {/* Question header */}
                            <div className="flex items-start gap-3">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary mt-0.5">
                                    {idx + 1}
                                </span>
                                <div className="flex-1 min-w-0 space-y-1.5">
                                    <p className="text-sm font-medium leading-snug text-foreground">
                                        {q.questionText}
                                    </p>
                                    <div className="flex flex-wrap gap-1 items-center">
                                        {q.questionFormat === 'mcq' && (
                                            <span className="inline-flex items-center gap-0.5 rounded-md border border-indigo-500/25 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 dark:text-indigo-400">
                                                <ListChecks className="h-2.5 w-2.5" /> MCQ
                                            </span>
                                        )}
                                        <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', TYPE_COLORS[q.questionType] || TYPE_COLORS.technical)}>
                                            {q.questionType}
                                        </span>
                                        <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', DIFF_COLORS[q.difficulty] || DIFF_COLORS.medium)}>
                                            {q.difficulty}
                                        </span>
                                        <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                                            <Clock className="h-2.5 w-2.5" /> {secsToLabel(q.timeLimitSeconds)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* MCQ options */}
                            {q.questionFormat === 'mcq' && q.options?.length > 0 && (
                                <div className="space-y-1.5 pl-9">
                                    {q.options.map((opt, i) => (
                                        <div key={i} className={cn(
                                            'flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm',
                                            opt.isCorrect
                                                ? 'border-emerald-500/40 bg-emerald-500/8 text-emerald-800 dark:text-emerald-300'
                                                : 'border-border bg-muted/30 text-foreground/70',
                                        )}>
                                            <span className={cn(
                                                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold',
                                                opt.isCorrect ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-border text-muted-foreground',
                                            )}>
                                                {opt.isCorrect ? <Check className="h-2.5 w-2.5" /> : ['A','B','C','D'][i]}
                                            </span>
                                            {opt.text}
                                            {opt.isCorrect && <span className="ml-auto text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">Correct</span>}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Ideal answer (verbal) */}
                            {q.questionFormat !== 'mcq' && q.idealAnswer && (
                                <div className="flex gap-2 pl-9 rounded-lg bg-muted/40 px-3 py-2.5">
                                    <Lightbulb className="h-3.5 w-3.5 shrink-0 text-amber-500 mt-0.5" />
                                    <p className="text-xs text-muted-foreground leading-relaxed">{q.idealAnswer}</p>
                                </div>
                            )}

                            {/* Keywords */}
                            {q.keywords?.length > 0 && (
                                <div className="flex flex-wrap gap-1 pl-9">
                                    <Tag className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                                    {q.keywords.map(kw => (
                                        <span key={kw} className="rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                            {kw}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default QuestionPreviewModal;
